import type { ErrorEvent, StackFrame } from "@sentry/nextjs";

/**
 * BROWSER EVENTS THAT ARE NOT A BUG IN THIS APP
 *
 * Sentry's browser SDK catches everything that reaches `window.onerror`, and
 * on a mobile web app that is more than our own code. The rules below drop
 * what has turned up so far and would otherwise arrive forever, because
 * nothing we can change stops it.
 *
 * THIS IS A BROWSER-ONLY FILE, deliberately, which is why it is not in
 * `sentry-shared.ts` with the options the three runtimes agree on. Rule 2 in
 * particular is only safe on the client — see its note.
 *
 * The bar for adding a rule here is that the event is NOT ACTIONABLE BY US and
 * we know why. A filter added because an error was noisy hides the next real
 * one that happens to look like it.
 */

/**
 * RULE 1 — script the in-app browser injected into our page.
 *
 * Facebook and Instagram render links inside their own WebView and inject a
 * performance logger into the page. When the user leaves, the native object
 * that logger posts to is torn down before the logger stops calling it, and
 * the resulting throw lands in our page's global scope — so our SDK reports it
 * as ours. It is not: `sendDataToNative` exists nowhere in this repository.
 *
 * Both platforms produced it (THETA-APP-3/4 Android, THETA-APP-6 iOS), and the
 * one that matters is the invitation link: an invitation arrives in a social
 * app, so an invited member opening it is the ordinary case, not the odd one.
 *
 * MATCH ON THE FUNCTION, NOT THE URL. The Android frames carry a distinctive
 * `app://navigation_performance_logger_android` filename, but the iOS ones are
 * attributed to OUR page URL (`app:///invitationer/link/<token>`), so a
 * `denyUrls` on the script name catches Android and misses iOS. The function
 * name is the only thing all three have in common.
 *
 * IF WE EVER SHIP OUR OWN NATIVE BRIDGE, do not name its function this. The
 * mobile port would then have its real errors swallowed here.
 */
const INJECTED_BRIDGE_FUNCTION = "sendDataToNative";

/** Android's injected logger, kept as a second signal for frames with no function name. */
const INJECTED_BRIDGE_SCRIPT = "navigation_performance_logger";

/** Every frame of every exception on the event, in one list. */
function framesOf(event: ErrorEvent): StackFrame[] {
  return (
    event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? []
  );
}

function isInjectedBridgeNoise(event: ErrorEvent): boolean {
  return framesOf(event).some(
    (frame) =>
      frame.function === INJECTED_BRIDGE_FUNCTION ||
      [frame.filename, frame.abs_path].some((path) =>
        path?.includes(INJECTED_BRIDGE_SCRIPT),
      ),
  );
}

/**
 * RULE 2 — a fetch aborted because the member navigated away.
 *
 * react-query cancels a query when its component unmounts, which on a phone is
 * what "tapped a link before the feed finished loading" looks like. The
 * rejection is an `AbortError` with no stack, attributed to whichever page they
 * left (THETA-APP-5, `/feed`, Mobile Safari). Nothing failed.
 *
 * SAFE ONLY IN THE BROWSER, and this is the reason the file is client-only:
 * an `AbortError` can also mean a REAL timeout, and every timeout-driven abort
 * we have is server-side — `AbortSignal.timeout` in the backend's link-preview
 * fetch, Linear calls, transcription and JWKS. Those report through
 * `backend/src/sentry.ts` and are untouched by this. No browser code aborts on
 * a timer (`useUserSearch` dropped the last hand-rolled `AbortController`), so
 * a browser AbortError is always a teardown. Add one and this rule stops being
 * true.
 *
 * The SDK reports these wrapped — type `Error`, message `AbortError: …` — so
 * the type alone does not match. Anchored to the start of the message, which
 * keeps an error that merely MENTIONS an abort reportable.
 */
function isTeardownAbort(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(
    (value) =>
      value.type === "AbortError" || /^AbortError\b/.test(value.value ?? ""),
  );
}

/**
 * RULE 3 — iOS Safari's `EmptyRanges`.
 *
 * A `ReferenceError: Can't find variable: EmptyRanges` from Mobile Safari
 * (THETA-APP-2, first seen 2026-08-28, back on 2026-10-04 from one iPhone).
 * The name is in neither our build output nor any installed dependency, and
 * the one frame has no file and no function: a script evaluated in our page by
 * the browser or an extension, not one we ship.
 *
 * The WHOLE message has to match, so a missing variable of our own whose name
 * merely starts like this one still gets reported.
 */
function isSafariEmptyRanges(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(
    (value) => value.value === "Can't find variable: EmptyRanges",
  );
}

/**
 * `beforeSend`: return the event to report it, `null` to drop it. Dropping also
 * drops the session replay that would have been attached, which is the point —
 * a masked recording of somebody's screen is not worth keeping for an error we
 * have already decided we will not act on.
 */
export function dropBrowserNoise(event: ErrorEvent): ErrorEvent | null {
  return isInjectedBridgeNoise(event) || isTeardownAbort(event) || isSafariEmptyRanges(event)
    ? null
    : event;
}
